import React, { useState, useEffect } from 'react';
import API from '../api';

const formatDoctorName = (d) => d ? `dr ${d.firstName} ${d.lastName}` : '';

const formatDayShort = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}.`;
};

// Prebacuje SVE "Kontrola" termine sa jednog doktora na drugog, na isti dan i isto vreme -
// poziva postojeći /reschedule endpoint za svaki termin. Endpoint već dozvoljava da više
// termina iste usluge na isto vreme koegzistira (grupni slot), pa se ovo ponaša ispravno
// i kad je kontrola grupna (više pacijenata na istoj satnici).
const ChangeDoctorModal = ({ open, date, sourceDoctorId, doctors, otherDoctors, kontrolaAppointments, onClose, onDone }) => {
    const [targetDoctorId, setTargetDoctorId] = useState('');
    const [moving, setMoving] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (open) {
            setTargetDoctorId('');
            setResult(null);
        }
    }, [open, date, sourceDoctorId]);

    if (!open) return null;

    const sourceDoctor = doctors.find(d => String(d.id) === String(sourceDoctorId));

    const confirmMove = async () => {
        if (!targetDoctorId) return;
        setMoving(true);
        setResult(null);
        const settled = await Promise.allSettled(
            kontrolaAppointments.map(a => {
                const startTime = a.startTime.split('T')[1].substring(0, 5);
                return API.put(`/appointments/${a.id}/reschedule`, { date, startTime, doctorId: targetDoctorId });
            })
        );
        const failed = settled.filter(r => r.status === 'rejected').length;
        const succeeded = settled.length - failed;
        setResult({ succeeded, failed });
        setMoving(false);
        if (succeeded > 0) onDone();
    };

    return (
        <div style={styles.backdrop} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h3 style={styles.title}>Promeni doktora za kontrole</h3>
                    <button style={styles.closeBtn} onClick={onClose}>✕</button>
                </div>

                <p style={styles.hint}>
                    Sve kontrole zakazane {formatDayShort(date)} kod {formatDoctorName(sourceDoctor)} biće prebačene
                    kod izabranog doktora, na isto vreme.
                </p>

                <div style={styles.columns}>
                    <div style={styles.column}>
                        <div style={styles.columnLabel}>Trenutni doktor</div>
                        <div style={styles.currentDoctorBox}>
                            <div style={styles.currentDoctorName}>{formatDoctorName(sourceDoctor)}</div>
                            <div style={styles.kontrolaCount}>
                                {kontrolaAppointments.length === 0
                                    ? 'Nema zakazanih kontrola ovog dana.'
                                    : `${kontrolaAppointments.length} ${kontrolaAppointments.length === 1 ? 'kontrola' : 'kontrola'} zakazano`}
                            </div>
                            {kontrolaAppointments.length > 0 && (
                                <ul style={styles.patientList}>
                                    {kontrolaAppointments.map(a => (
                                        <li key={a.id} style={styles.patientItem}>
                                            {a.startTime.split('T')[1].substring(0, 5)} — {a.patientFullName}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div style={styles.arrow}>→</div>

                    <div style={styles.column}>
                        <div style={styles.columnLabel}>Novi doktor</div>
                        {otherDoctors.length === 0 && (
                            <div style={styles.noOthers}>Nijedan drugi doktor ne radi ovog dana.</div>
                        )}
                        {otherDoctors.map(d => (
                            <div
                                key={d.id}
                                onClick={() => setTargetDoctorId(String(d.id))}
                                style={{
                                    ...styles.doctorOption,
                                    ...(String(targetDoctorId) === String(d.id) ? styles.doctorOptionActive : {})
                                }}
                            >
                                {formatDoctorName(d)}
                            </div>
                        ))}
                    </div>
                </div>

                {result && (
                    <div style={result.failed > 0 ? styles.resultWarn : styles.resultOk}>
                        Prebačeno: {result.succeeded}.{result.failed > 0 && ` Nije uspelo: ${result.failed} (verovatno je novi doktor već zauzet u tom terminu).`}
                    </div>
                )}

                <div style={styles.actions}>
                    <button
                        style={{ ...styles.confirmBtn, opacity: (!targetDoctorId || moving || kontrolaAppointments.length === 0) ? 0.6 : 1 }}
                        onClick={confirmMove}
                        disabled={!targetDoctorId || moving || kontrolaAppointments.length === 0}
                    >
                        {moving ? 'Prebacivanje...' : 'Prebaci kontrole'}
                    </button>
                    <button style={styles.cancelBtn} onClick={onClose}>Zatvori</button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    backdrop: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '15px'
    },
    modal: {
        backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '560px',
        maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', padding: '20px'
    },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
    title: { margin: 0, fontSize: '17px', color: '#1e293b' },
    closeBtn: { background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#94a3b8', padding: '4px 8px' },
    hint: { fontSize: '13px', color: '#64748b', marginBottom: '16px', lineHeight: '1.5' },
    columns: { display: 'flex', gap: '10px', alignItems: 'stretch', marginBottom: '14px' },
    column: { flex: 1, minWidth: 0 },
    columnLabel: { fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' },
    arrow: { display: 'flex', alignItems: 'center', color: '#cbd5e1', fontSize: '20px' },
    currentDoctorBox: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' },
    currentDoctorName: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' },
    kontrolaCount: { fontSize: '12px', color: '#64748b', marginBottom: '6px' },
    patientList: { margin: 0, paddingLeft: '16px' },
    patientItem: { fontSize: '12px', color: '#334155', marginBottom: '2px' },
    noOthers: { fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' },
    doctorOption: {
        padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff',
        color: '#1e293b', fontSize: '13px', cursor: 'pointer', marginBottom: '6px'
    },
    doctorOptionActive: { border: '1px solid #2563eb', backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: '600' },
    resultOk: { backgroundColor: '#dcfce7', color: '#166534', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', fontWeight: '600', marginBottom: '12px' },
    resultWarn: { backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', fontWeight: '600', marginBottom: '12px' },
    actions: { display: 'flex', gap: '10px' },
    confirmBtn: { flex: 1, padding: '10px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
    cancelBtn: { padding: '10px 16px', backgroundColor: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }
};

export default ChangeDoctorModal;
